import { useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { SlidePanel, ConfirmDialog } from "../SlidePanel";
import { Btn } from "../UI";
import { printPrescriptions, layoutLabel } from "../../lib/rxPrint";
import { RxPrintDialog } from "./RxPrintDialog";
import { useAuth } from "../../context/AuthContext";
import { RxEmailForm, RxCallForm, RxVoidForm, RxDiscontinueForm } from "./RxCommForms";
import { RxLines } from "./RxLines";
import { RxPharmacyGroups } from "./RxPharmacyGroups";
import { RxStatusChip, fmtDate, fmtDateTime } from "./rxShared";
import { Printer, Mail, Phone, CheckCircle2, Pencil, PenLine, Trash2, Radio, Ban, RotateCw } from "lucide-react";

function CommLog({ rx }) {
  if (!rx.communications?.length) return <p className="text-xs text-[#84675B]" data-testid="rx-comm-empty">No pharmacy communication logged yet.</p>;
  return (
    <ol className="space-y-2" data-testid="rx-comm-log">
      {rx.communications.map((c) => (
        <li key={c.id} className="rounded-xl border border-[#EFEBE0] p-3 text-xs text-[#5D4037]" data-testid={`rx-comm-${c.id}`}>
          <div className="flex items-center gap-1.5 font-semibold text-[#3E2723]">{c.channel === "email" ? <Mail size={12} /> : <Phone size={12} />}
            {c.outcome}{c.delivery === "failed" && <span className="text-[#C62828]"> ({c.error})</span>}</div>
          <div>{c.pharmacy_name} · {c.to || "no contact"} · by {c.staff_name} · {fmtDateTime(c.created_at)}</div>
          {c.medications?.length > 0 && <div className="text-[#7B6157]">For: {c.medications.join(", ")}</div>}
          {c.contact_name && <div>Spoke with {c.contact_name}</div>}{c.note && <div className="italic">{c.note}</div>}
        </li>
      ))}
    </ol>
  );
}

const joinNames = (xs) => (xs.length < 3 ? xs.join(" and ") : `${xs.slice(0, -1).join(", ")}, and ${xs[xs.length - 1]}`);

function VoidBanner({ rx }) {
  const contacted = (rx.communications || []).some((c) => (c.channel === "phone" || c.delivery === "sent") && c.created_at < rx.voided_at);
  const list = (rx.pharmacy_groups || []).filter((g) => g.pharmacy).map((g) => g.pharmacy.name);
  const names = `${joinNames(list)} ${list.length > 1 ? "were" : "was"}`;
  return (
    <div className="rounded-xl border border-[#FFCDD2] bg-[#FFEBEE] p-3 text-xs text-[#B71C1C]" data-testid="rx-void-banner">
      <div className="flex items-center gap-1.5 font-bold uppercase"><Ban size={12} /> Voided {fmtDateTime(rx.voided_at)} by {rx.voided_by}</div>
      <div className="mt-0.5">Reason: {rx.void_reason}.{(rx.voided_meds_count ?? 1) > 0 ? " Linked medications were marked discontinued." : ""}</div>
      {contacted && list.length > 0 && !rx.void_call_logged && <div className="mt-1 font-semibold" data-testid="rx-void-pharmacy-reminder">{names} already contacted. Let them know it is voided and log the call below.</div>}
      {contacted && rx.void_call_logged && <div className="mt-1 font-semibold" data-testid="rx-void-pharmacy-done">The pharmacy was told about the void.</div>}
    </div>
  );
}

function Links({ rx, onOpen }) {
  if (!rx.renewed_from && !rx.renewals?.length && !rx.duplicated_from?.length) return null;
  const a = (x) => <button type="button" data-testid={`rx-link-${x.rx_number}`} onClick={() => onOpen(x.id)} className="font-mono font-semibold text-[#4E342E] underline">{x.rx_number}</button>;
  const list = (xs) => xs.map((x, i) => <span key={x.id}>{i ? ", " : ""}{a(x)}</span>);
  return (
    <div className="flex flex-wrap gap-x-4 text-xs text-[#7B6157]" data-testid="rx-renew-links">
      {rx.renewed_from && <span>Renewal of {a(rx.renewed_from)}</span>}
      {rx.renewals?.length > 0 && <span>Renewed as {list(rx.renewals)}</span>}
      {rx.duplicated_from?.length > 0 && <span>Duplicated from {list(rx.duplicated_from)}</span>}
    </div>
  );
}

function ModeForm({ mode, rx, meta, onDone, onCancel }) {
  if (mode.kind === "void") return <RxVoidForm rx={rx} onDone={onDone} onCancel={onCancel} />;
  if (mode.kind === "discontinue") return <RxDiscontinueForm rx={rx} line={mode.line} onDone={onDone} onCancel={onCancel} />;
  if (mode.kind === "email") return <RxEmailForm rx={rx} group={mode.group} onDone={onDone} onCancel={onCancel} />;
  if (mode.kind === "call") return <RxCallForm key={`${mode.group.pharmacy.id}-${mode.outcome || ""}`} rx={rx} group={mode.group} outcomes={meta.call_outcomes} initialOutcome={mode.outcome} onDone={onDone} onCancel={onCancel} />;
  return null;
}

/** Prescription detail: lines, pharmacies, print, email, call log, discontinue, void, and status history. */
export function RxDetail({ rx, open, onClose, onChange, onEdit, onSign, onRenewed, onOpen, meta, me, pharmacies }) {
  const [mode, setMode] = useState(null);
  const [del, setDel] = useState(false);
  const [printing, setPrinting] = useState(false);
  const { user } = useAuth();
  if (!rx) return null;
  const draft = rx.status === "draft";
  const voided = rx.status === "voided";
  const done = rx.status === "completed" || voided;
  const active = rx.lines.filter((l) => !l.discontinued);
  const canVoid = !draft && !voided && (rx.prescriber_id === me.id || me.role === "admin");
  const renew = async () => { try { const r = await api.post(`/prescriptions/${rx.id}/renew`); toast.success(`Draft ${r.data.rx_number} created from ${rx.rx_number}`); onRenewed(r.data); } catch (e) { toast.error(apiError(e)); } };
  const run = async (fn, msg) => { try { const r = await fn(); if (msg) toast.success(msg); onChange(r.data); } catch (e) { toast.error(apiError(e)); } };
  const doPrint = (o) => {
    setPrinting(false);
    const lines = voided ? rx.lines : active;
    printPrescriptions([{ rx, lines }], { ...o, printedBy: `${user.first_name || ""} ${user.last_name || ""}`.trim() });
    run(() => api.post(`/prescriptions/${rx.id}/printed`, voided ? { voided_copy: true, layout: layoutLabel(o) } : { layout: layoutLabel(o) }));
  };
  const finish = (data) => { setMode(null); onChange(data); };
  const callFor = (pid) => setMode({ kind: "call", outcome: "Remaining refills cancelled", group: rx.pharmacy_groups.find((g) => g.pharmacy?.id === pid) });
  return (
    <SlidePanel open={open} onClose={onClose} width="max-w-2xl" testId="rx-detail" title={`Prescription ${rx.rx_number}`}
      subtitle={`${rx.client_name} · ${rx.prescriber_name}${rx.signed_at ? ` · signed ${fmtDateTime(rx.signed_at)}` : ""}`}>
      <div className="space-y-5">
        <div className="flex flex-wrap items-center gap-2"><RxStatusChip rx={rx} testId="rx-detail-status" />
          {rx.written_date && <span className="text-xs text-[#7B6157]" data-testid="rx-written-date-label">Written {fmtDate(rx.written_date)}</span>}
          {rx.visit_label && <span className="text-xs text-[#7B6157]">Visit: {rx.visit_label}</span>}
          {rx.print_count > 0 && <span className="text-xs text-[#7B6157]">Printed {rx.print_count}x</span>}</div>
        <Links rx={rx} onOpen={onOpen} />
        {voided && <VoidBanner rx={rx} />}
        <RxLines rx={rx} canDiscontinue={!draft && !voided && me.can_edit} onDiscontinue={(line) => setMode({ kind: "discontinue", line })} onCallPharmacy={callFor} />
        {rx.notes && <p className="rounded-xl bg-[#F9F5EE] p-3 text-xs text-[#5D4037]">{rx.notes}</p>}
        {draft ? (
          <div className="flex flex-wrap gap-2">
            {me.is_prescriber && <Btn variant="outline" data-testid="rx-edit-btn" onClick={onEdit}><Pencil size={14} /> Edit draft</Btn>}
            {rx.prescriber_id === me.id && <Btn data-testid="rx-review-sign-btn" onClick={onSign}><PenLine size={14} /> Review and sign</Btn>}
            {me.is_prescriber && <Btn variant="ghost" data-testid="rx-delete-btn" onClick={() => setDel(true)}><Trash2 size={14} /> Delete draft</Btn>}
            {rx.prescriber_id !== me.id && <p className="w-full text-xs text-[#7B6157]">Only {rx.prescriber_name} can sign this prescription.</p>}
          </div>
        ) : (
          <>
            <RxPharmacyGroups rx={rx} pharmacies={pharmacies} onMode={setMode} run={run} />
            <div className="flex flex-wrap gap-2">
              {!voided && <Btn variant="outline" data-testid="rx-print-btn" disabled={!active.length} onClick={() => setPrinting(true)}><Printer size={14} /> Print</Btn>}
              {voided && <Btn variant="outline" data-testid="rx-print-voided-btn" onClick={() => setPrinting(true)}><Printer size={14} /> Print voided copy</Btn>}
              {!done && <Btn variant="ghost" data-testid="rx-complete-btn" onClick={() => run(() => api.post(`/prescriptions/${rx.id}/complete`), "Marked completed")}><CheckCircle2 size={14} /> Mark completed</Btn>}
              {me.is_prescriber && !voided && active.length > 0 && <Btn variant="outline" data-testid="rx-renew-btn" onClick={renew}><RotateCw size={14} /> Renew</Btn>}
              {canVoid && <Btn variant="ghost" data-testid="rx-void-btn" onClick={() => setMode({ kind: "void" })} className="!text-[#C62828]"><Ban size={14} /> Void</Btn>}
            </div>
            {!voided && <p className="flex items-start gap-1.5 text-[11px] text-[#7B6157]" data-testid="rx-erx-note"><Radio size={12} className="mt-0.5 shrink-0" /> {meta.electronic.note}</p>}
          </>
        )}
        {mode && <ModeForm mode={mode} rx={rx} meta={meta} onDone={finish} onCancel={() => setMode(null)} />}
        {!draft && <div><div className="mb-2 text-[11px] font-bold uppercase text-[#7B6157]">Pharmacy communication</div><CommLog rx={rx} /></div>}
        <div><div className="mb-2 text-[11px] font-bold uppercase text-[#7B6157]">Status history</div>
          <ol className="space-y-1 text-xs text-[#5D4037]" data-testid="rx-status-history">
            {(rx.status_history || []).map((h, i) => <li key={i}><strong>{h.label}</strong> · {h.by} · {fmtDateTime(h.at)}{h.reason ? ` · ${h.reason}` : ""}</li>)}
          </ol></div>
      </div>
      <RxPrintDialog open={printing} count={voided ? rx.lines.length : active.length} voided={voided} onClose={() => setPrinting(false)} onPrint={doPrint} />
      <ConfirmDialog open={del} onClose={() => setDel(false)} title={`Delete draft ${rx.rx_number}?`} description="The draft is removed. Signed prescriptions can never be deleted."
        onConfirm={async () => { try { await api.delete(`/prescriptions/${rx.id}`); toast.success("Draft deleted"); setDel(false); onChange(null); } catch (e) { toast.error(apiError(e)); } }} />
    </SlidePanel>
  );
}
